import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaSearch, FaTimes, FaLink, FaChevronDown } from 'react-icons/fa';
import { FaPlay, FaTag, FaMapMarkerAlt, FaClipboardList } from 'react-icons/fa';
import { isStoreOpen } from '../../utils/storeTimeUtils';
import { searchProducts, clearProducts, fetchProducts, fetchOfferProducts, setOffersMode } from '../../redux/menu/ProductSlice';
import { setLanguage } from '../../redux/menu/ProfileSlice';
import useTheme from '../../hooks/useTheme';
import { getNavbarBackgroundColor } from '../../utils/backgroundStyles';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
    const { t, i18n } = useTranslation();
    const [changeHeader, setChangeHeader] = useState(false)
    const [isSearchExpanded, setIsSearchExpanded] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [isMobile, setIsMobile] = useState(false)
    const [isLangOpen, setIsLangOpen] = useState(false);
    const langRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { profile, language, enableArabic } = useSelector((state) => state.profile);
    const isArabic = language === 'ar';
    const { categories } = useSelector(state => state.category);
    const { status, isSearchMode, isOffersMode } = useSelector(state => state.product);
    const prevIsSearchMode = useRef(isSearchMode);
    
    // Get navbar background color based on coverImage
    const coverImage = profile?.cover_image || profile?.theme?.coverImage || 'foody';
    const navbarBgColor = getNavbarBackgroundColor(coverImage);
    const isNoBackground = coverImage === 'noBackground';
    
    // Check if we're on the home screen (route pattern: /:id)
    const isHomeScreen = location.pathname === `/${profile?.key}` || location.pathname === '/';
    
    // Check if store is currently open
    const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);
    
    // Check if store status is available (not false)
    const storeStatusAvailable = profile?.status !== false;
    
    // Store is available for orders if it's open and status is available
    const storeAvailable = storeIsOpen && storeStatusAvailable;

    // Sync Redux language with i18next and handle document direction
    useEffect(() => {
        if (enableArabic) {
            i18n.changeLanguage(language);
            document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
            document.documentElement.lang = language;
            return;
        }
        i18n.changeLanguage('en');
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = 'en';
    }, [language, enableArabic, i18n]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (langRef.current && !langRef.current.contains(event.target)) {
                setIsLangOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    //header change function 
    const onChangeHeader = () => {
        if (window.scrollY >= 50) {
            setChangeHeader(true)
        } else {
            setChangeHeader(false)
        }
    }

    //change header by scrolling
    window.addEventListener('scroll', onChangeHeader)

    // Check if device is mobile
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 768)
        }
        
        checkIsMobile()
        window.addEventListener('resize', checkIsMobile)
        
        return () => window.removeEventListener('resize', checkIsMobile)
    }, [])

    const handleReels = () => {
        navigate(`${profile?.key}/reels`);
    };

    const handleConnect = () => {
        navigate(`${profile?.key}/connect`);
    };

    const handleMyOrders = () => {
        navigate(`${profile?.key}/my-orders`);
    };

    const handleMap = () => {
        if (profile?.map_url) {
            window.open(profile.map_url, '_blank', 'noopener,noreferrer');
        } else {
            navigate(`${profile?.key}/connect`);
        }
    };

    const handleOffers = () => {
        if (isOffersMode) {
            dispatch(setOffersMode(false));
            if (categories && categories.length > 0) {
                dispatch(clearProducts());
                dispatch(fetchProducts({ 
                    categoryID: categories[0].id, 
                    page: 1 
                }));
            }
        } else {
            dispatch(setOffersMode(true));
            dispatch(fetchOfferProducts({}));
        }
    };

    const handleLanguageChange = (lang) => {
        dispatch(setLanguage(lang));
        setIsLangOpen(false);
    };

    // Search functionality
    const handleSearch = () => { 
        if (searchText.trim()) {
            dispatch(clearProducts())
            dispatch(searchProducts({ 
                text: searchText.trim() 
            }))
        } else {
            handleClearSearch()
        }
    }

    const handleClearSearch = useCallback(() => {
        if (categories && categories.length > 0) {
            dispatch(clearProducts())
            dispatch(fetchProducts({ 
                categoryID: categories[0].id, 
                page: 1 
            }))
        }
        setSearchText('')
        setIsSearchExpanded(false)
    }, [categories, dispatch])

    useEffect(() => {
        if (searchText === '' && isSearchMode) {
            handleClearSearch()
        }
    }, [searchText, isSearchMode, handleClearSearch])

    useEffect(() => {
        if (prevIsSearchMode.current === true && isSearchMode === false && searchText !== '') {
            setSearchText('')
            setIsSearchExpanded(false)
        }
        prevIsSearchMode.current = isSearchMode;
    }, [isSearchMode, searchText])

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch()
        }
    }

    const toggleSearch = () => {
        setIsSearchExpanded(!isSearchExpanded)
        if (isSearchExpanded) {
            handleClearSearch()
        }
    }

    const languages = [
        { code: 'en', label: 'English', flag: '🇺🇸' },
        { code: 'ar', label: 'العربية', flag: '🇸🇦' }
    ];

    return (
        <header className={changeHeader || isNoBackground ? "fixed top-0 left-0 w-full transition duration-500" : "bg-transparent fixed top-0 left-0 w-full transition duration-500"} style={{ zIndex: 9999, backgroundColor: (changeHeader || isNoBackground) ? (theme.name === 'cookbook' || theme.name === 'palm' ? theme.colors.navbarBg : (isNoBackground ? '#Fafafa' : navbarBgColor)) : 'transparent', boxShadow: 'none' }}>
            <nav className="flex items-center max-w-screen-xl mx-auto px-3 sm:px-4 md:px-6 py-2 md:py-3">
                {/* left  */}
                <div className="flex flex-grow">
                    <img 
                        className="w-16 h-14 sm:w-18 sm:h-16 md:w-20 md:h-16 lg:w-28 lg:h-24 cursor-pointer object-contain" 
                        src={profile?.DocLogo?.imageUrl } 
                        alt={profile?.name || "logo"} 
                        onClick={() => navigate(`/${profile?.key}`)} 
                    />
                </div>
                {/* right  */}
                {storeAvailable && (
                    <div className={`flex items-center space-x-1 sm:space-x-2 md:space-x-3 ${isArabic ? 'space-x-reverse' : ''}`}>
                        {/* Search Button/Input - Only show on home screen */}
                        {isHomeScreen && (
                            <div className="relative">
                                {isMobile ? (
                                    <div className={`flex items-center space-x-1 ${isArabic ? 'space-x-reverse' : ''} backdrop-blur-sm border rounded-full shadow-sm px-2 py-1.5`}
                                         style={{
                                             backgroundColor: theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                             borderColor: theme.colors.borderColor
                                         }}>
                                        <input 
                                            type="text" 
                                            className="outline-none bg-transparent text-xs w-20 placeholder-gray-400" 
                                            style={{ color: theme.colors.textPrimary }}
                                            placeholder={t('common.search')} 
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                        />
                                            <button 
                                                onClick={handleSearch}
                                                className={`text-xs py-0.5 px-2 rounded-full poppins transition duration-300 ${
                                                    status === 'loading' && isSearchMode ? 'bg-gray-400 cursor-not-allowed' : ''
                                                }`}
                                                style={status !== 'loading' || !isSearchMode ? {
                                                    backgroundColor: theme.colors.searchButtonBg,
                                                    color: theme.colors.searchButtonText,
                                                    border: `1px solid ${theme.colors.searchButtonBorder}`
                                                } : {}}
                                                disabled={status === 'loading' && isSearchMode}
                                            >
                                                <FaSearch className="w-3 h-3 transform transition-transform duration-300" />
                                            </button>
                                        {searchText && (
                                            <button 
                                                onClick={handleClearSearch}
                                                className="text-gray-400 hover:text-gray-600 transition-colors"
                                            >
                                                <FaTimes className="w-2.5 h-2.5" />
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        {!isSearchExpanded ? (
                                            <button 
                                                onClick={toggleSearch}
                                                className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} px-3 md:px-4 py-1.5 md:py-2.5 backdrop-blur-sm border rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105`}
                                                style={{
                                                    backgroundColor: theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                                    borderColor: theme.colors.borderColor,
                                                    color: theme.colors.textPrimary
                                                }}
                                            >
                                                <FaSearch className="w-4 h-4 transform transition-transform duration-300" style={{ color: theme.colors.searchIconColor }} />
                                                <span className="poppins font-medium text-sm" style={{ color: theme.colors.searchTextColor }}>{t('common.search')}</span>
                                            </button>
                                        ) : (
                                            <div className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} backdrop-blur-sm border rounded-full shadow-sm px-3 md:px-4 py-1.5 md:py-2.5`}
                                                 style={{
                                                     backgroundColor: theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                                     borderColor: theme.colors.borderColor
                                                 }}>
                                                <input 
                                                    type="text" 
                                                    className="outline-none bg-transparent text-sm w-32 lg:w-48 placeholder-gray-400" 
                                                    style={{ color: theme.colors.textPrimary }}
                                                    placeholder={t('common.search')} 
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    onKeyPress={handleKeyPress}
                                                    autoFocus
                                                />
                                                <button 
                                                    onClick={handleSearch}
                                                    className={`text-xs py-1 px-3 rounded-full poppins transition duration-300 ${
                                                        status === 'loading' && isSearchMode ? 'bg-gray-400 cursor-not-allowed' : ''
                                                    }`}
                                                    style={status !== 'loading' || !isSearchMode ? {
                                                        backgroundColor: theme.colors.searchButtonBg,
                                                        color: theme.colors.searchButtonText,
                                                        border: `1px solid ${theme.colors.searchButtonBorder}`
                                                    } : {}}
                                                    disabled={status === 'loading' && isSearchMode}
                                                >
                                                    {status === 'loading' && isSearchMode ? '...' : t('common.go')}
                                                </button>
                                                <button 
                                                    onClick={toggleSearch}
                                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <FaTimes className="w-3 h-3" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Offers Button */}
                        {isHomeScreen && (
                            <button 
                                onClick={handleOffers}
                                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-full shadow-sm transform transition-all duration-300 hover:scale-105"
                                style={{
                                    backgroundColor: isOffersMode ? theme.colors.navOffersBgActive : theme.colors.navOffersBgInactive,
                                    borderColor: isOffersMode ? theme.colors.navOffersBorderActive : theme.colors.navOffersBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                            >
                                <FaTag 
                                    className={`w-3 h-3 sm:w-4 sm:h-4 transform transition-transform duration-300 ${isArabic ? 'rotate-90' : ''}`} 
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                />
                                <span 
                                    className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium poppins`}
                                    style={{ color: isOffersMode ? theme.colors.navOffersIconActive : theme.colors.navOffersIconInactive }}
                                >
                                    {t('common.offers')}
                                </span>
                            </button>
                        )}
                        
                        {/* Desktop/Tablet Main Nav */}
                        <div className={`hidden md:flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''}`}>
                            {profile?.reel > 0 ? (
                                <button 
                                    onClick={handleReels}
                                    className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105`}
                                    style={{
                                        backgroundColor: location.pathname.includes('/reels') ? theme.colors.navReelsBgActive : theme.colors.navReelsBgInactive,
                                        borderColor: location.pathname.includes('/reels') ? theme.colors.navReelsBorderActive : theme.colors.navReelsBorderInactive,
                                        borderWidth: '1px',
                                        borderStyle: 'solid'
                                    }}
                                >
                                    <FaPlay 
                                        className="w-3 h-3 md:w-4 md:h-4 transform transition-transform duration-300" 
                                        style={{ color: location.pathname.includes('/reels') ? theme.colors.navReelsIconActive : theme.colors.navReelsIconInactive }} 
                                    />
                                    <span 
                                        className="hidden lg:block poppins font-medium text-sm" 
                                        style={{ color: location.pathname.includes('/reels') ? theme.colors.navReelsIconActive : theme.colors.navReelsIconInactive }}
                                    >
                                        {t('common.reels')}
                                    </span>
                                </button>
                            ) : (
                                <button 
                                    onClick={handleMap}
                                    className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105`}
                                    style={{
                                        backgroundColor: location.pathname.includes('/connect') ? theme.colors.navMapBgActive : theme.colors.navMapBgInactive,
                                        borderColor: location.pathname.includes('/connect') ? theme.colors.navMapBorderActive : theme.colors.navMapBorderInactive,
                                        borderWidth: '1px',
                                        borderStyle: 'solid'
                                    }}
                                >
                                    <FaMapMarkerAlt 
                                        className="w-3 h-3 md:w-4 md:h-4 transform transition-transform duration-300" 
                                        style={{ color: location.pathname.includes('/connect') ? theme.colors.navMapIconActive : theme.colors.navMapIconInactive }} 
                                    />
                                    <span 
                                        className="hidden lg:block poppins font-medium text-sm" 
                                        style={{ color: location.pathname.includes('/connect') ? theme.colors.navMapIconActive : theme.colors.navMapIconInactive }}
                                    >
                                        {t('common.map')}
                                    </span>
                                </button>
                            )}
                            {profile?.track_order && (
                                <button 
                                    onClick={handleMyOrders}
                                    className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105`}
                                    style={{
                                        backgroundColor: location.pathname.includes('/my-orders') ? theme.colors.navConnectBgActive : theme.colors.navConnectBgInactive,
                                        borderColor: location.pathname.includes('/my-orders') ? theme.colors.navConnectBorderActive : theme.colors.navConnectBorderInactive,
                                        borderWidth: '1px',
                                        borderStyle: 'solid'
                                    }}
                                >
                                    <FaClipboardList 
                                        className="w-3 h-3 md:w-4 md:h-4 transform transition-transform duration-300" 
                                        style={{ color: location.pathname.includes('/my-orders') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }} 
                                    />
                                    <span 
                                        className="hidden lg:block poppins font-medium text-sm" 
                                        style={{ color: location.pathname.includes('/my-orders') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }}
                                    >
                                        {t('common.my_orders')}
                                    </span>
                                </button>
                            )}
                            <button 
                                onClick={handleConnect}
                                className={`flex items-center space-x-2 ${isArabic ? 'space-x-reverse' : ''} px-3 md:px-4 py-1.5 md:py-2.5 rounded-full shadow-sm hover:shadow-md transform transition-all duration-300 hover:scale-105`}
                                style={{
                                    backgroundColor: location.pathname.includes('/connect') ? theme.colors.navConnectBgActive : theme.colors.navConnectBgInactive,
                                    borderColor: location.pathname.includes('/connect') ? theme.colors.navConnectBorderActive : theme.colors.navConnectBorderInactive,
                                    borderWidth: '1px',
                                    borderStyle: 'solid'
                                }}
                            >
                                <FaLink 
                                    className="w-3 h-3 md:w-4 md:h-4 transform transition-transform duration-300" 
                                    style={{ color: location.pathname.includes('/connect') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }} 
                                    />
                                <span 
                                    className="hidden lg:block poppins font-medium text-sm" 
                                    style={{ color: location.pathname.includes('/connect') ? theme.colors.navConnectIconActive : theme.colors.navConnectIconInactive }}
                                >
                                    {t('common.connect')}
                                </span>
                            </button>
                        </div>

                        {/* Language Selector Dropdown - Moved to Last with "Different Menu" look */}
                        <div className={`flex items-center ${isMobile ? 'ml-1' : 'ml-3 sm:ml-4'}`} ref={langRef}>
                            {/* Vertical Divider */}
                            <div className="h-6 w-[1px] bg-gray-300 mr-1 sm:mr-3 md:block"></div>
                            
                            <div className="relative">
                                <button 
                                    onClick={() => setIsLangOpen(!isLangOpen)}
                                    className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2.5 rounded-full shadow-sm hover:shadow-md border transition-all duration-300"
                                    style={{
                                        backgroundColor: theme.name === 'cookbook' || theme.name === 'palm' ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                        borderColor: theme.colors.borderColor
                                    }}
                                    title={language === 'en' ? "Language / اللغة" : "اللغة / Language"}
                                >
                                    <span className="text-base sm:text-lg">
                                        {languages.find(l => l.code === language)?.flag}
                                    </span>
                                    <span className="hidden lg:block text-sm font-semibold poppins" style={{ color: theme.colors.textPrimary }}>
                                        {languages.find(l => l.code === language)?.label}
                                    </span>
                                    <FaChevronDown className={`w-2.5 h-2.5 text-gray-400 transform transition-transform duration-300 ${isLangOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {isLangOpen && (
                                    <div 
                                        className={`absolute top-full mt-2 ${i18n.language === 'ar' ? 'left-0' : 'right-0'} rounded-2xl shadow-2xl border py-2.5 min-w-[160px] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300`}
                                        style={{
                                            backgroundColor: theme.colors.surfaceColor,
                                            borderColor: theme.colors.borderColor,
                                            zIndex: 10000
                                        }}
                                    >
                                        {languages.map((lang) => (
                                            <button
                                                key={lang.code}
                                                onClick={() => handleLanguageChange(lang.code)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-200 ${
                                                    language === lang.code 
                                                        ? 'font-bold' 
                                                        : 'hover:bg-gray-500/10'
                                                } ${i18n.language === 'ar' ? 'flex-row-reverse' : ''}`}
                                                style={{
                                                    color: language === lang.code ? theme.colors.primaryColor : theme.colors.textSecondary,
                                                    backgroundColor: language === lang.code ? `${theme.colors.primaryColor}10` : 'transparent'
                                                }}
                                            >
                                                <span className="text-xl transform transition-transform group-hover:scale-110">{lang.flag}</span>
                                                <span className="poppins flex-grow text-left">{lang.label}</span>
                                                {language === lang.code && (
                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary shadow-sm" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </header>
    )
}

export default Navbar;
